import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Card — primitivo compositivo con subcomponentes.
 *
 * Anatomia: Card.Root (= Card) [Card.Media] [Card.Header] [Card.Body] [Card.Footer].
 * No incluye CTA / titulo por defecto: cada variante (ProductCard, ServiceCard, etc.)
 * se construye sobre este primitivo. Aquellas variantes se crean en F4.
 *
 * Estados visuales se activan con la prop `interactive` (hover/focus shadow).
 * Soporta renderizar como `article`, `a`, `button` o `div`.
 */
export type CardElement = 'article' | 'div' | 'a' | 'button' | 'aside' | 'section';

export interface CardRootProps extends HTMLAttributes<HTMLElement> {
  /** Elemento HTML semantico subyacente. */
  as?: CardElement;
  /** Activa estilos hover/focus (sombra eleva, sutil scale). */
  interactive?: boolean;
  /** Marca la card como seleccionada (borde violeta 2px). */
  selected?: boolean;
  /** Si `as="a"`, link destino. */
  href?: string;
  /** Si `as="a"` y abre en nueva pestana. */
  external?: boolean;
  children: ReactNode;
}

const CardRoot = forwardRef<HTMLElement, CardRootProps>(
  (
    {
      as = 'article',
      interactive = false,
      selected = false,
      href,
      external = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = cn(
      // Base: fondo crema, sombra elev1, esquinas redondeadas, overflow para que Card.Media respete radius.
      'bg-cream-50 text-primary-900 rounded-lg shadow-elev1 overflow-hidden',
      'transition-[box-shadow,transform,border-color] duration-2 ease-out',
      interactive && ['hover:shadow-elev2 focus-within:shadow-elev2', 'hover:-translate-y-0.5'],
      selected ? 'border-2 border-primary-700' : 'border border-transparent',
      className,
    );

    if (as === 'a') {
      const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          {...externalProps}
          {...(props as HTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </a>
      );
    }
    if (as === 'button') {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={classes}
          {...(props as HTMLAttributes<HTMLButtonElement>)}
        >
          {children}
        </button>
      );
    }
    if (as === 'div') {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          className={classes}
          {...(props as HTMLAttributes<HTMLDivElement>)}
        >
          {children}
        </div>
      );
    }
    return (
      <article ref={ref as React.Ref<HTMLElement>} className={classes} {...props}>
        {children}
      </article>
    );
  },
);
CardRoot.displayName = 'Card';

/** Media slot: imagen/portada con ratio definido por el consumidor. */
const CardMedia = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative w-full overflow-hidden bg-cream-200', className)}
      {...props}
    >
      {children}
    </div>
  ),
);
CardMedia.displayName = 'Card.Media';

/** Header slot: titulo / chips superiores. */
const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 pt-6', className)} {...props}>
      {children}
    </div>
  ),
);
CardHeader.displayName = 'Card.Header';

/** Body slot: contenido principal (descripcion, meta, etc.). */
const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 py-4 text-body', className)} {...props}>
      {children}
    </div>
  ),
);
CardBody.displayName = 'Card.Body';

/** Footer slot: acciones (CTA). */
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 pb-6 pt-2 flex items-center gap-3', className)} {...props}>
      {children}
    </div>
  ),
);
CardFooter.displayName = 'Card.Footer';

// Compound: Card.Media / Card.Header / Card.Body / Card.Footer
type CardComponent = typeof CardRoot & {
  Media: typeof CardMedia;
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

export const Card = CardRoot as CardComponent;
Card.Media = CardMedia;
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export { CardMedia, CardHeader, CardBody, CardFooter };
